import { type CSSProperties, useRef } from "react";
import countriesData from "../../../worker/games/worldle/countries.json";
import { useWorldShapes } from "../geo/useWorldShapes";
import { type Box, cameraBox, fitTransform, playerRoute } from "./route";
import "./puzzle.css";

const VW = 360;
const VH = 230;
const BORDERS = new Map((countriesData as { code: string; borders?: string[] }[]).map((c) => [c.code, c.borders ?? []]));
const bordersOf = (code: string) => BORDERS.get(code) ?? [];

interface PuzzleMapProps {
  from: string;
  to: string;
  /** Accepted countries, in guess order. */
  path: string[];
  /** Countries that didn't fit, in guess order. */
  misses: string[];
  /** The shortest route's middle countries, once the game is over. */
  answer: string[] | null;
  status: "playing" | "won" | "lost";
}

/**
 * Travle as a jigsaw: A and B sit on a map of empty slots; every accepted
 * country drops in as a flag-textured piece and snaps, a country that doesn't
 * border the route wobbles and falls away. The camera frames the pieces.
 */
export function PuzzleMap({ from, to, path, misses, answer, status }: PuzzleMapProps) {
  const shapes = useWorldShapes();

  // Pieces present on first render are restored, not dropped.
  const initial = useRef<{ placed: Set<string>; misses: number } | null>(null);
  initial.current ??= { placed: new Set(path), misses: misses.length };

  const finished = status !== "playing";
  const ghosts = status === "lost" && answer ? answer.filter((c) => !path.includes(c)) : [];
  const focus = [from, to, ...path, ...ghosts];
  const box = cameraBox(
    focus.map((c) => shapes.by.get(c)?.box).filter((b): b is Box => Boolean(b)),
    0.2,
    70,
  );
  const cam = box ? fitTransform(box, VW, VH) : { k: 1, x: 0, y: 0 };

  const route = status === "won" ? playerRoute(from, to, path, bordersOf) : null;
  const routeD = route
    ?.map((code, i) => {
      const c = shapes.by.get(code)?.c;
      return c ? `${i === 0 ? "M" : "L"}${c[0].toFixed(1)} ${c[1].toFixed(1)}` : "";
    })
    .join(" ");
  const lastMissIndex = misses.length - 1;
  const lastMiss = lastMissIndex >= (initial.current.misses ?? 0) ? misses[lastMissIndex] : undefined;
  const flagged = [...new Set([from, to, ...path, ...(lastMiss ? [lastMiss] : [])])];

  const piece = (code: string, className: string) => {
    const s = shapes.by.get(code);
    if (!s) return null;
    return (
      <g key={`${className}:${code}`} className={`pz-piece ${className}`}>
        <path d={s.d} fill={`url(#pz-f-${code})`} vectorEffect="non-scaling-stroke" />
      </g>
    );
  };

  return (
    <div className={`pz ${status === "won" ? "pz-won" : ""}`}>
      <svg className="pz-board" viewBox={`0 0 ${VW} ${VH}`} role="img" aria-label="мапа маршруту">
        <defs>
          {flagged.map((code) => (
            <pattern key={code} id={`pz-f-${code}`} patternContentUnits="objectBoundingBox" width="1" height="1">
              <image href={`/flags/${code.toLowerCase()}.svg`} width="1" height="1" preserveAspectRatio="xMidYMid slice" />
            </pattern>
          ))}
        </defs>
        <g
          className="pz-camera"
          style={{ transform: `translate(${cam.x}px, ${cam.y}px) scale(${cam.k})`, "--k": cam.k } as CSSProperties}
        >
          {shapes.all.map((f, i) => (
            <path key={i} className="pz-slot" d={f.d} vectorEffect="non-scaling-stroke" />
          ))}
          {ghosts.map((code) => {
            const s = shapes.by.get(code);
            return s ? (
              <g key={`ghost:${code}`} className="pz-ghost">
                <path d={s.d} vectorEffect="non-scaling-stroke" />
              </g>
            ) : null;
          })}
          {piece(from, "")}
          {piece(to, finished ? "" : "pz-goal")}
          {path.map((code) => piece(code, initial.current?.placed.has(code) ? "" : "pz-drop"))}
          {lastMiss && !finished && piece(lastMiss, `pz-reject pz-miss-${lastMissIndex}`)}
          {routeD && (
            <>
              <path className="pz-route-under pz-route-draw" d={routeD} vectorEffect="non-scaling-stroke" />
              <path className="pz-route pz-route-draw" d={routeD} vectorEffect="non-scaling-stroke" />
              {route?.map((code) => {
                const c = shapes.by.get(code)?.c;
                return c ? <circle key={`stop:${code}`} className="pz-stop" cx={c[0]} cy={c[1]} r={4.2 / cam.k} vectorEffect="non-scaling-stroke" /> : null;
              })}
            </>
          )}
        </g>
      </svg>
    </div>
  );
}
